import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import React__default, { useRef, useEffect, useState, useCallback, useMemo, Fragment as Fragment$1, Children, useContext, createContext } from "react";
import { Button } from "../Components/ui/button.js";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "../Components/ui/command.js";
import { DropdownMenuItem, DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../Components/ui/dropdown-menu.js";
import { HoverCardTrigger, HoverCard, HoverCardContent } from "../Components/ui/hover-card.js";
import { InputGroupButton, InputGroupAddon, InputGroupTextarea } from "../Components/ui/input-group.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../Components/ui/select.js";
import { cn } from "../lib/utils.js";
import { ImageIcon, PlusIcon, XIcon, PaperclipIcon, MicIcon, SendIcon, Loader2Icon, SquareIcon } from "lucide-react";
import { nanoid } from "nanoid";
const PromptInputController = createContext(null);
const ProviderAttachmentsContext = createContext(null);
const usePromptInputController = () => {
  const ctx = useContext(PromptInputController);
  if (!ctx) {
    throw new Error("Wrap your component inside <PromptInputProvider> to use usePromptInputController().");
  }
  return ctx;
};
const useOptionalPromptInputController = () => useContext(PromptInputController);
const useProviderAttachments = () => {
  const ctx = useContext(ProviderAttachmentsContext);
  if (!ctx) {
    throw new Error("Wrap your component inside <PromptInputProvider> to use useProviderAttachments().");
  }
  return ctx;
};
const useOptionalProviderAttachments = () => useContext(ProviderAttachmentsContext);
function PromptInputProvider({ initialInput: initialTextInput = "", children }) {
  const [textInput, setTextInput] = useState(initialTextInput);
  const clearInput = useCallback(() => setTextInput(""), []);
  const [attachements, setAttachements] = useState([]);
  const fileInputRef = useRef(null);
  const openRef = useRef(() => {
  });
  const add = useCallback((files) => {
    const incoming = Array.from(files);
    if (incoming.length === 0) return;
    setAttachements(
      (prev) => prev.concat(
        incoming.map((file) => ({
          id: nanoid(),
          type: "file",
          url: URL.createObjectURL(file),
          mediaType: file.type,
          filename: file.name
        }))
      )
    );
  }, []);
  const remove = useCallback((id) => {
    setAttachements((prev) => {
      const found = prev.find((f) => f.id === id);
      if (found == null ? void 0 : found.url) URL.revokeObjectURL(found.url);
      return prev.filter((f) => f.id !== id);
    });
  }, []);
  const clear = useCallback(() => {
    setAttachements((prev) => {
      for (const f of prev) if (f.url) URL.revokeObjectURL(f.url);
      return [];
    });
  }, []);
  const openFileDialog = useCallback(() => {
    var _a;
    (_a = openRef.current) == null ? void 0 : _a.call(openRef);
  }, []);
  const attachments = useMemo(
    () => ({
      files: attachements,
      add,
      remove,
      clear,
      openFileDialog,
      fileInputRef
    }),
    [attachements, add, remove, clear, openFileDialog]
  );
  const __registerFileInput = useCallback((ref, open) => {
    fileInputRef.current = ref.current;
    openRef.current = open;
  }, []);
  const controller = useMemo(
    () => ({
      textInput: {
        value: textInput,
        setInput: setTextInput,
        clear: clearInput
      },
      attachments,
      __registerFileInput
    }),
    [textInput, clearInput, attachments, __registerFileInput]
  );
  return /* @__PURE__ */ jsx(PromptInputController.Provider, { value: controller, children: /* @__PURE__ */ jsx(ProviderAttachmentsContext.Provider, { value: attachments, children }) });
}
const LocalAttachmentsContext = createContext(null);
const usePromptInputAttachments = () => {
  const provider = useOptionalProviderAttachments();
  const local = useContext(LocalAttachmentsContext);
  const context = provider ?? local;
  if (!context) {
    throw new Error("usePromptInputAttachments must be used within a PromptInput or PromptInputProvider");
  }
  return context;
};
function PromptInputAttachment({ data, className, ...props }) {
  var _a;
  const attachments = usePromptInputAttachments();
  const filename = data.filename || "";
  const mediaType = ((_a = data.mediaType) == null ? void 0 : _a.startsWith("image/")) && data.url ? "image" : "file";
  const isImage = mediaType === "image";
  if (isImage) {
    return /* @__PURE__ */ jsx("div", { style: { padding: 6, alignSelf: "flex-start" }, children: /* @__PURE__ */ jsxs(PromptInputHoverCard, { openDelay: 200, children: [
      /* @__PURE__ */ jsx(HoverCardTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "tw:relative tw:flex tw:justify-end tw:items-start tw:shrink-0 tw:rounded-lg tw:border tw:border-border tw:overflow-hidden tw:bg-background tw:cursor-pointer",
            className
          ),
          style: { width: 84, height: 84, padding: 6 },
          ...props,
          children: [
            /* @__PURE__ */ jsx(
              "img",
              {
                alt: filename || "attachment",
                src: data.url,
                style: {
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 8
                }
              }
            ),
            /* @__PURE__ */ jsxs(
              "button",
              {
                "aria-label": "Remove attachment",
                onClick: (e) => {
                  e.stopPropagation();
                  attachments.remove(data.id);
                },
                onPointerEnter: (e) => e.stopPropagation(),
                onPointerLeave: (e) => e.stopPropagation(),
                onMouseEnter: (e) => e.stopPropagation(),
                onMouseLeave: (e) => e.stopPropagation(),
                type: "button",
                style: {
                  position: "relative",
                  zIndex: 10,
                  width: 20,
                  height: 20,
                  minWidth: 20,
                  cursor: "pointer",
                  borderRadius: "50%",
                  padding: 0,
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                },
                children: [
                  /* @__PURE__ */ jsx(XIcon, { style: { width: 12, height: 12 } }),
                  /* @__PURE__ */ jsx("span", { className: "tw:sr-only", children: "Remove" })
                ]
              }
            )
          ]
        },
        data.id
      ) }),
      /* @__PURE__ */ jsx(
        PromptInputHoverCardContent,
        {
          className: "tw:w-auto tw:p-2",
          side: "top",
          sideOffset: 8,
          collisionPadding: 16,
          avoidCollisions: true,
          children: /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-md", children: /* @__PURE__ */ jsx(
            "img",
            {
              alt: filename || "attachment preview",
              src: data.url,
              style: { maxHeight: 280, maxWidth: 280, objectFit: "contain" }
            }
          ) })
        }
      )
    ] }) });
  }
  const attachmentLabel = filename || "Attachment";
  return /* @__PURE__ */ jsxs(PromptInputHoverCard, { children: [
    /* @__PURE__ */ jsx(HoverCardTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "tw:group tw:relative tw:flex tw:h-8 tw:cursor-default tw:select-none tw:items-center tw:gap-1.5 tw:rounded-md tw:border tw:border-border tw:px-1.5 tw:font-medium tw:text-sm tw:transition-all tw:hover:bg-accent tw:hover:text-accent-foreground tw:dark:hover:bg-accent/50",
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsxs("div", { className: "tw:relative tw:size-5 tw:shrink-0", children: [
            /* @__PURE__ */ jsx("div", { className: "tw:absolute tw:inset-0 tw:flex tw:size-5 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded tw:bg-background tw:transition-opacity tw:group-hover:opacity-0", children: /* @__PURE__ */ jsx("div", { className: "tw:flex tw:size-5 tw:items-center tw:justify-center tw:text-muted-foreground", children: /* @__PURE__ */ jsx(PaperclipIcon, { className: "tw:size-3" }) }) }),
            /* @__PURE__ */ jsxs(
              Button,
              {
                "aria-label": "Remove attachment",
                className: "tw:absolute tw:inset-0 tw:size-5 tw:cursor-pointer tw:rounded tw:p-0 tw:opacity-0 tw:transition-opacity tw:group-hover:pointer-events-auto tw:group-hover:opacity-100 tw:[&>svg]:size-2.5",
                onClick: (e) => {
                  e.stopPropagation();
                  attachments.remove(data.id);
                },
                type: "button",
                variant: "ghost",
                children: [
                  /* @__PURE__ */ jsx(XIcon, {}),
                  /* @__PURE__ */ jsx("span", { className: "tw:sr-only", children: "Remove" })
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsx("span", { className: "tw:flex-1 tw:truncate", children: attachmentLabel })
        ]
      },
      data.id
    ) }),
    /* @__PURE__ */ jsx(PromptInputHoverCardContent, { className: "tw:w-auto tw:p-2", children: /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:gap-2.5", children: /* @__PURE__ */ jsxs("div", { className: "tw:min-w-0 tw:flex-1 tw:space-y-1 tw:px-0.5", children: [
      /* @__PURE__ */ jsx("h4", { className: "tw:truncate tw:font-semibold tw:text-sm tw:leading-none", children: attachmentLabel }),
      data.mediaType && /* @__PURE__ */ jsx("p", { className: "tw:truncate tw:font-mono tw:text-muted-foreground tw:text-xs", children: data.mediaType })
    ] }) }) })
  ] });
}
function PromptInputAttachments({ children }) {
  const attachments = usePromptInputAttachments();
  if (!attachments.files.length) {
    return null;
  }
  return attachments.files.map((file) => /* @__PURE__ */ jsx(Fragment$1, { children: children(file) }, file.id));
}
const PromptInputActionAddAttachments = ({
  label = "Add photos or files",
  ...props
}) => {
  const attachments = usePromptInputAttachments();
  return /* @__PURE__ */ jsxs(
    DropdownMenuItem,
    {
      ...props,
      onSelect: (e) => {
        e.preventDefault();
        attachments.openFileDialog();
      },
      children: [
        /* @__PURE__ */ jsx(ImageIcon, { className: "tw:mr-2 tw:size-4" }),
        " ",
        label
      ]
    }
  );
};
const PromptInput = ({
  className,
  accept,
  multiple,
  globalDrop,
  syncHiddenInput,
  maxFiles,
  maxFileSize,
  onError,
  onSubmit,
  children,
  ...props
}) => {
  const controller = useOptionalPromptInputController();
  const usingProvider = !!controller;
  const inputRef = useRef(null);
  const anchorRef = useRef(null);
  const formRef = useRef(null);
  useEffect(() => {
    var _a;
    const root = (_a = anchorRef.current) == null ? void 0 : _a.closest("form");
    if (root instanceof HTMLFormElement) {
      formRef.current = root;
    }
  }, []);
  const [items, setItems] = useState([]);
  const files = usingProvider ? controller.attachments.files : items;
  const openFileDialogLocal = useCallback(() => {
    var _a;
    (_a = inputRef.current) == null ? void 0 : _a.click();
  }, []);
  const matchesAccept = useCallback(
    (f) => {
      if (!accept || accept.trim() === "") {
        return true;
      }
      if (accept.includes("image/*")) {
        return f.type.startsWith("image/");
      }
      return true;
    },
    [accept]
  );
  const addLocal = useCallback(
    (fileList) => {
      const incoming = Array.from(fileList);
      const accepted = incoming.filter((f) => matchesAccept(f));
      if (incoming.length && accepted.length === 0) {
        onError == null ? void 0 : onError({
          code: "accept",
          message: "No files match the accepted types."
        });
        return;
      }
      const withinSize = (f) => maxFileSize ? f.size <= maxFileSize : true;
      const sized = accepted.filter(withinSize);
      if (accepted.length > 0 && sized.length === 0) {
        onError == null ? void 0 : onError({
          code: "max_file_size",
          message: "All files exceed the maximum size."
        });
        return;
      }
      setItems((prev) => {
        const capacity = typeof maxFiles === "number" ? Math.max(0, maxFiles - prev.length) : void 0;
        const capped = typeof capacity === "number" ? sized.slice(0, capacity) : sized;
        if (typeof capacity === "number" && sized.length > capacity) {
          onError == null ? void 0 : onError({
            code: "max_files",
            message: "Too many files. Some were not added."
          });
        }
        const next = [];
        for (const file of capped) {
          next.push({
            id: nanoid(),
            type: "file",
            url: URL.createObjectURL(file),
            mediaType: file.type,
            filename: file.name
          });
        }
        return prev.concat(next);
      });
    },
    [matchesAccept, maxFiles, maxFileSize, onError]
  );
  const add = usingProvider ? (files2) => controller.attachments.add(files2) : addLocal;
  const remove = usingProvider ? (id) => controller.attachments.remove(id) : (id) => setItems((prev) => {
    const found = prev.find((file) => file.id === id);
    if (found == null ? void 0 : found.url) {
      URL.revokeObjectURL(found.url);
    }
    return prev.filter((file) => file.id !== id);
  });
  const clear = usingProvider ? () => controller.attachments.clear() : () => setItems((prev) => {
    for (const file of prev) {
      if (file.url) {
        URL.revokeObjectURL(file.url);
      }
    }
    return [];
  });
  const openFileDialog = usingProvider ? () => controller.attachments.openFileDialog() : openFileDialogLocal;
  useEffect(() => {
    if (!usingProvider) return;
    controller.__registerFileInput(inputRef, () => {
      var _a;
      return (_a = inputRef.current) == null ? void 0 : _a.click();
    });
  }, [usingProvider, controller]);
  useEffect(() => {
    if (syncHiddenInput && inputRef.current && files.length === 0) {
      inputRef.current.value = "";
    }
  }, [files, syncHiddenInput]);
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const onDragOver = (e) => {
      var _a, _b;
      if ((_b = (_a = e.dataTransfer) == null ? void 0 : _a.types) == null ? void 0 : _b.includes("Files")) {
        e.preventDefault();
      }
    };
    const onDrop = (e) => {
      var _a, _b, _c;
      if ((_b = (_a = e.dataTransfer) == null ? void 0 : _a.types) == null ? void 0 : _b.includes("Files")) {
        e.preventDefault();
      }
      if (((_c = e.dataTransfer) == null ? void 0 : _c.files) && e.dataTransfer.files.length > 0) {
        add(e.dataTransfer.files);
      }
    };
    form.addEventListener("dragover", onDragOver);
    form.addEventListener("drop", onDrop);
    return () => {
      form.removeEventListener("dragover", onDragOver);
      form.removeEventListener("drop", onDrop);
    };
  }, [add]);
  useEffect(() => {
    if (!globalDrop) return;
    const onDragOver = (e) => {
      var _a, _b;
      if ((_b = (_a = e.dataTransfer) == null ? void 0 : _a.types) == null ? void 0 : _b.includes("Files")) {
        e.preventDefault();
      }
    };
    const onDrop = (e) => {
      var _a, _b, _c;
      if ((_b = (_a = e.dataTransfer) == null ? void 0 : _a.types) == null ? void 0 : _b.includes("Files")) {
        e.preventDefault();
      }
      if (((_c = e.dataTransfer) == null ? void 0 : _c.files) && e.dataTransfer.files.length > 0) {
        add(e.dataTransfer.files);
      }
    };
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("drop", onDrop);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("drop", onDrop);
    };
  }, [add, globalDrop]);
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(
    () => () => {
      if (!usingProvider) {
        for (const f of filesRef.current) {
          if (f.url && f.url.startsWith("blob:")) URL.revokeObjectURL(f.url);
        }
      }
    },
    [usingProvider]
  );
  const handleChange = (event) => {
    if (event.currentTarget.files) {
      add(event.currentTarget.files);
    }
  };
  const convertBlobUrlToDataUrl = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
  const ctx = useMemo(
    () => ({
      files: files.map((item) => ({ ...item, id: item.id })),
      add,
      remove,
      clear,
      openFileDialog,
      fileInputRef: inputRef
    }),
    [files, add, remove, clear, openFileDialog]
  );
  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const text = usingProvider ? controller.textInput.value : (() => {
      const formData = new FormData(form);
      return formData.get("message") || "";
    })();
    if (!usingProvider) {
      form.reset();
    }
    Promise.all(
      files.map(async ({ id, ...item }) => {
        if (item.url && item.url.startsWith("blob:")) {
          return {
            ...item,
            url: await convertBlobUrlToDataUrl(item.url)
          };
        }
        return item;
      })
    ).then((convertedFiles) => {
      try {
        const result = onSubmit({ text, files: convertedFiles }, event);
        if (result instanceof Promise) {
          result.then(() => {
            clear();
            if (usingProvider) {
              controller.textInput.clear();
            }
          }).catch(() => {
          });
        } else {
          clear();
          if (usingProvider) {
            controller.textInput.clear();
          }
        }
      } catch (error) {
      }
    });
  };
  const inner = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("span", { "aria-hidden": "true", className: "tw:hidden", ref: anchorRef }),
    /* @__PURE__ */ jsx(
      "input",
      {
        accept,
        "aria-label": "Upload files",
        className: "tw:hidden",
        multiple,
        onChange: handleChange,
        ref: inputRef,
        title: "Upload files",
        type: "file"
      }
    ),
    /* @__PURE__ */ jsx("form", { className: cn("tw:w-full", className), onSubmit: handleSubmit, ...props, children })
  ] });
  return usingProvider ? inner : /* @__PURE__ */ jsx(LocalAttachmentsContext.Provider, { value: ctx, children: inner });
};
const PromptInputBody = ({ className, ...props }) => /* @__PURE__ */ jsx("div", { className: cn("tw:contents", className), ...props });
const PromptInputTextarea = React__default.forwardRef(
  ({ onChange, className, placeholder = "What would you like to know?", ...props }, ref) => {
    const controller = useOptionalPromptInputController();
    const attachments = usePromptInputAttachments();
    const [isComposing, setIsComposing] = useState(false);
    const handleKeyDown = (e) => {
      var _a;
      if (e.key === "Enter") {
        if (isComposing || e.nativeEvent.isComposing) {
          return;
        }
        if (e.shiftKey) {
          return;
        }
        e.preventDefault();
        (_a = e.currentTarget.form) == null ? void 0 : _a.requestSubmit();
      }
      if (e.key === "Backspace" && e.currentTarget.value === "" && attachments.files.length > 0) {
        e.preventDefault();
        const lastAttachment = attachments.files.at(-1);
        if (lastAttachment) {
          attachments.remove(lastAttachment.id);
        }
      }
    };
    const handlePaste = (event) => {
      var _a;
      const items = (_a = event.clipboardData) == null ? void 0 : _a.items;
      if (!items) {
        return;
      }
      const files = [];
      for (const item of items) {
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
      if (files.length > 0) {
        event.preventDefault();
        attachments.add(files);
      }
    };
    const controlledProps = controller ? {
      value: controller.textInput.value,
      onChange: (e) => {
        controller.textInput.setInput(e.currentTarget.value);
        onChange == null ? void 0 : onChange(e);
      }
    } : {
      onChange
    };
    return /* @__PURE__ */ jsx(
      InputGroupTextarea,
      {
        ref,
        className: cn("tw:field-sizing-content tw:max-h-48 tw:min-h-16", className),
        name: "message",
        onCompositionEnd: () => setIsComposing(false),
        onCompositionStart: () => setIsComposing(true),
        onKeyDown: handleKeyDown,
        onPaste: handlePaste,
        placeholder,
        ...props,
        ...controlledProps
      }
    );
  }
);
PromptInputTextarea.displayName = "PromptInputTextarea";
const PromptInputHeader = ({ className, ...props }) => /* @__PURE__ */ jsx(InputGroupAddon, { align: "block-end", className: cn("tw:order-first tw:flex-wrap tw:gap-1", className), ...props });
const PromptInputFooter = ({ className, ...props }) => /* @__PURE__ */ jsx(InputGroupAddon, { align: "block-end", className: cn("tw:justify-between tw:gap-1", className), ...props });
const PromptInputTools = ({ className, ...props }) => /* @__PURE__ */ jsx("div", { className: cn("tw:flex tw:items-center tw:gap-1", className), ...props });
const PromptInputButton = ({ variant = "ghost", className, size, ...props }) => {
  const newSize = size ?? (Children.count(props.children) > 1 ? "sm" : "icon-sm");
  return /* @__PURE__ */ jsx(InputGroupButton, { className: cn(className), size: newSize, type: "button", variant, ...props });
};
const PromptInputActionMenu = (props) => /* @__PURE__ */ jsx(DropdownMenu, { ...props });
const PromptInputActionMenuTrigger = ({ className, children, ...props }) => /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(PromptInputButton, { className, ...props, children: children ?? /* @__PURE__ */ jsx(PlusIcon, { className: "tw:size-4" }) }) });
const PromptInputActionMenuContent = ({ className, ...props }) => /* @__PURE__ */ jsx(DropdownMenuContent, { align: "start", className: cn(className), ...props });
const PromptInputActionMenuItem = ({ className, ...props }) => /* @__PURE__ */ jsx(DropdownMenuItem, { className: cn(className), ...props });
const PromptInputSubmit = ({
  className,
  variant = "default",
  size = "icon-sm",
  status,
  onStop,
  children,
  ...props
}) => {
  let Icon = /* @__PURE__ */ jsx(SendIcon, { className: "tw:size-4" });
  if (status === "submitted") {
    Icon = /* @__PURE__ */ jsx(Loader2Icon, { className: "tw:size-4 tw:animate-spin" });
  } else if (status === "streaming") {
    Icon = /* @__PURE__ */ jsx(SquareIcon, { className: "tw:size-4 tw:!fill-white tw:!stroke-white" });
  } else if (status === "error") {
    Icon = /* @__PURE__ */ jsx(XIcon, { className: "tw:size-4" });
  }
  const isStopButton = status === "streaming" && onStop;
  return /* @__PURE__ */ jsx(
    InputGroupButton,
    {
      "aria-label": isStopButton ? "Stop" : "Submit",
      className: cn(className),
      size,
      type: isStopButton ? "button" : "submit",
      onClick: isStopButton ? onStop : void 0,
      variant,
      ...props,
      disabled: isStopButton ? false : props.disabled,
      children: children ?? Icon
    }
  );
};
const PromptInputSpeechButton = ({
  className,
  textareaRef,
  onTranscriptionChange,
  onListeningChange,
  children,
  ...props
}) => {
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const recognitionRef = useRef(null);
  useEffect(() => {
    if (typeof window !== "undefined" && ("SpeechRecognition" in window || "webkitSpeechRecognition" in window)) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const speechRecognition = new SpeechRecognition();
      speechRecognition.continuous = true;
      speechRecognition.interimResults = true;
      speechRecognition.lang = "en-US";
      speechRecognition.onstart = () => {
        setIsListening(true);
        onListeningChange == null ? void 0 : onListeningChange(true);
      };
      speechRecognition.onend = () => {
        setIsListening(false);
        onListeningChange == null ? void 0 : onListeningChange(false);
      };
      speechRecognition.onresult = (event) => {
        var _a;
        let finalTranscript = "";
        const results = Array.from(event.results);
        for (const result of results) {
          if (result.isFinal) {
            finalTranscript += ((_a = result[0]) == null ? void 0 : _a.transcript) ?? "";
          }
        }
        if (finalTranscript && (textareaRef == null ? void 0 : textareaRef.current)) {
          const textarea = textareaRef.current;
          const currentValue = textarea.value;
          const newValue = currentValue + (currentValue ? " " : "") + finalTranscript;
          textarea.value = newValue;
          textarea.dispatchEvent(new Event("input", { bubbles: true }));
          onTranscriptionChange == null ? void 0 : onTranscriptionChange(newValue);
        }
      };
      speechRecognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
        onListeningChange == null ? void 0 : onListeningChange(false);
      };
      recognitionRef.current = speechRecognition;
      setRecognition(speechRecognition);
    }
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [textareaRef, onTranscriptionChange, onListeningChange]);
  const toggleListening = useCallback(() => {
    if (!recognition) {
      return;
    }
    if (isListening) {
      recognition.stop();
    } else {
      recognition.start();
    }
  }, [recognition, isListening]);
  const renderedChildren = typeof children === "function" ? children(isListening) : children ?? /* @__PURE__ */ jsx(MicIcon, { className: "tw:size-4" });
  return /* @__PURE__ */ jsx(
    PromptInputButton,
    {
      className: cn(
        "tw:relative tw:transition-all tw:duration-200",
        isListening && "tw:animate-pulse tw:bg-accent tw:text-accent-foreground",
        className
      ),
      disabled: !recognition,
      onClick: toggleListening,
      ...props,
      children: renderedChildren
    }
  );
};
const PromptInputModelSelect = (props) => /* @__PURE__ */ jsx(Select, { ...props });
const PromptInputModelSelectTrigger = ({ className, ...props }) => /* @__PURE__ */ jsx(
  SelectTrigger,
  {
    className: cn(
      "tw:border-none tw:bg-transparent tw:font-medium tw:text-muted-foreground tw:shadow-none tw:transition-colors",
      "tw:hover:bg-accent tw:hover:text-foreground tw:[&[aria-expanded=true]]:bg-accent tw:[&[aria-expanded=true]]:text-foreground",
      className
    ),
    ...props
  }
);
const PromptInputModelSelectContent = ({ className, ...props }) => /* @__PURE__ */ jsx(SelectContent, { className: cn(className), ...props });
const PromptInputModelSelectItem = ({ className, ...props }) => /* @__PURE__ */ jsx(SelectItem, { className: cn(className), ...props });
const PromptInputModelSelectValue = ({ className, ...props }) => /* @__PURE__ */ jsx(SelectValue, { className: cn(className), ...props });
const PromptInputHoverCard = ({ openDelay = 0, closeDelay = 0, ...props }) => /* @__PURE__ */ jsx(HoverCard, { closeDelay, openDelay, ...props });
const PromptInputHoverCardTrigger = (props) => /* @__PURE__ */ jsx(HoverCardTrigger, { ...props });
const PromptInputHoverCardContent = ({ align = "start", ...props }) => /* @__PURE__ */ jsx(HoverCardContent, { align, ...props });
const PromptInputTabsList = ({ className, ...props }) => /* @__PURE__ */ jsx("div", { className: cn(className), ...props });
const PromptInputTab = ({ className, ...props }) => /* @__PURE__ */ jsx("div", { className: cn(className), ...props });
const PromptInputTabLabel = ({ className, ...props }) => /* @__PURE__ */ jsx("h3", { className: cn("tw:mb-2 tw:px-3 tw:font-medium tw:text-muted-foreground tw:text-xs", className), ...props });
const PromptInputTabBody = ({ className, ...props }) => /* @__PURE__ */ jsx("div", { className: cn("tw:space-y-1", className), ...props });
const PromptInputTabItem = ({ className, ...props }) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn("tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-2 tw:text-xs tw:hover:bg-accent", className),
    ...props
  }
);
const PromptInputCommand = ({ className, ...props }) => /* @__PURE__ */ jsx(Command, { className: cn(className), ...props });
const PromptInputCommandInput = ({ className, ...props }) => /* @__PURE__ */ jsx(CommandInput, { className: cn(className), ...props });
const PromptInputCommandList = ({ className, ...props }) => /* @__PURE__ */ jsx(CommandList, { className: cn(className), ...props });
const PromptInputCommandEmpty = ({ className, ...props }) => /* @__PURE__ */ jsx(CommandEmpty, { className: cn(className), ...props });
const PromptInputCommandGroup = ({ className, ...props }) => /* @__PURE__ */ jsx(CommandGroup, { className: cn(className), ...props });
const PromptInputCommandItem = ({ className, ...props }) => /* @__PURE__ */ jsx(CommandItem, { className: cn(className), ...props });
const PromptInputCommandSeparator = ({ className, ...props }) => /* @__PURE__ */ jsx(CommandSeparator, { className: cn(className), ...props });
export {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuItem,
  PromptInputActionMenuTrigger,
  PromptInputAttachment,
  PromptInputAttachments,
  PromptInputBody,
  PromptInputButton,
  PromptInputCommand,
  PromptInputCommandEmpty,
  PromptInputCommandGroup,
  PromptInputCommandInput,
  PromptInputCommandItem,
  PromptInputCommandList,
  PromptInputCommandSeparator,
  PromptInputFooter,
  PromptInputHeader,
  PromptInputHoverCard,
  PromptInputHoverCardContent,
  PromptInputHoverCardTrigger,
  PromptInputModelSelect,
  PromptInputModelSelectContent,
  PromptInputModelSelectItem,
  PromptInputModelSelectTrigger,
  PromptInputModelSelectValue,
  PromptInputProvider,
  PromptInputSpeechButton,
  PromptInputSubmit,
  PromptInputTab,
  PromptInputTabBody,
  PromptInputTabItem,
  PromptInputTabLabel,
  PromptInputTabsList,
  PromptInputTextarea,
  PromptInputTools,
  usePromptInputAttachments,
  usePromptInputController,
  useProviderAttachments
};
//# sourceMappingURL=prompt-input.js.map
